import { useState } from 'react'
import { IconCheck, IconCopy } from './icons'
import { Button } from './ui'

/**
 * The fallback when a set-up invite email did not go out: the claim link, shown
 * once, with a copy button and a short warning to share it privately.
 *
 * ⚠️ THE LINK IS A WORKING CREDENTIAL UNTIL IT IS USED — whoever opens it first
 * chooses the account's password. It lives only in the props this is given (the
 * one response that carried it); nothing here stores or logs it.
 *
 * ⚠️ PRESENTATION ONLY, like the rest of the kit: `name` and `expires` arrive
 * already formatted.
 */
export function InviteLinkFallback({ url, name, expires }: { url: string; name: string; expires?: string | null }) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle')
  const copy = () => {
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied('copied')
        window.setTimeout(() => {
          setCopied('idle')
        }, 2000)
      })
      .catch(() => {
        setCopied('failed')
      })
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3.5 font-dash">
      <p className="text-[13px] leading-5 font-medium text-ink">Share the invite link yourself</p>
      <div className="flex flex-wrap gap-2">
        <input
          readOnly
          value={url}
          aria-label="Invite link"
          onFocus={(e) => {
            e.currentTarget.select()
          }}
          className="h-8 min-w-0 flex-1 basis-56 rounded-md border border-line-strong bg-surface-subtle px-2.5 text-[12.5px] text-ink"
        />
        <Button size="sm" icon={copied === 'copied' ? IconCheck : IconCopy} onClick={copy}>
          {copied === 'copied' ? 'Copied' : 'Copy invite link'}
        </Button>
      </div>
      <p role={copied === 'failed' ? 'alert' : undefined} className="text-[12px] leading-4 text-muted">
        {copied === 'failed' ? 'Could not copy — select the link and copy it by hand. ' : ''}
        Anyone with this link can set the password for {name}’s login. Send it to them privately, and only to them. It works once
        {expires ? ` and stops working on ${expires}` : ''}.
      </p>
    </div>
  )
}
